/**
 * Leading + trailing throttle. The first call in a quiet window fires
 * `fn` synchronously; subsequent calls inside `intervalMs` collapse into
 * a single trailing invocation at the end of the window.
 *
 * Use for "coalesce a burst of events into at most ~2 actions per
 * window" patterns — e.g. invalidating a TanStack Query in response to a
 * stream of pg_notify events. Keeps a 100-row batch (~100 NOTIFYs in
 * <1s) to ~2 refetches instead of 100, while still being responsive on
 * the leading edge.
 *
 * Returns a `cancel()` to clear any pending trailing call (call from a
 * React effect cleanup so an unmount can't fire `fn` after teardown).
 *
 * `intervalMs <= 0` disables throttling — every call is forwarded
 * synchronously.
 */
export function leadingTrailingThrottle(
  fn: () => void,
  intervalMs: number,
  opts: { now?: () => number } = {},
): { call: () => void; cancel: () => void } {
  const now = opts.now ?? Date.now;
  // Sentinel that guarantees the first `call()` always fires as a
  // leading edge — `Date.now() - (-Infinity) === Infinity`, which is
  // unconditionally `>= intervalMs`. Initializing to `0` would tie the
  // leading-edge contract to the wall clock and break under any test
  // setup (or contrived runtime) where `Date.now()` returns a small
  // value at construction time.
  let lastRun = Number.NEGATIVE_INFINITY;
  let trailingTimer: ReturnType<typeof setTimeout> | null = null;

  const run = () => {
    lastRun = now();
    fn();
  };

  return {
    call() {
      if (intervalMs <= 0) {
        run();
        return;
      }
      const elapsed = now() - lastRun;
      if (elapsed >= intervalMs) {
        run();
        return;
      }
      if (trailingTimer) return;
      trailingTimer = setTimeout(() => {
        trailingTimer = null;
        run();
      }, intervalMs - elapsed);
    },
    cancel() {
      if (trailingTimer) {
        clearTimeout(trailingTimer);
        trailingTimer = null;
      }
    },
  };
}
